import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useState,
} from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useTranslation } from 'react-i18next';

import {
  CurrencyCodeEnum,
  RelatedProductCategoryNameEnum,
} from '@electricjs/core_entity-client';

import { useGlobalUI } from '@/components/GlobalUIProvider';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { useGetOrganizationAdditionalInfoQuery } from '@/redux/slices/organizationApiSlice';
import { type CatalogCartEntry, type AssetCartEntry } from '@/types/storefront';
import { type Product } from '@/types/products';
import { type StorefrontAsset } from '@/types/assets';

import {
  assetCartContainsWarehouseAsset,
  createCatalogCartEntry,
  getItemRelatedProducts,
} from '../utils';

import { StoreDrawerContent } from './StoreDrawerContent';

type StorefrontCartContextType = {
  catalogCart: CatalogCartEntry[];
  setCatalogCart: (cart: CatalogCartEntry[]) => void;
  assetCart: AssetCartEntry[];
  setAssetCart: (cart: AssetCartEntry[]) => void;
  canConfigureWinZtp: boolean;
  setCanConfigureWinZtp: (value: boolean) => void;
  canConfigureWinAutopilot: boolean;
  setCanConfigureWinAutopilot: (value: boolean) => void;
  catalogShippingCost?: number;
  setCatalogShippingCost: (value: number | undefined) => void;
  warehouseShippingCost?: number;
  setWarehouseShippingCost: (value: number | undefined) => void;
  orderTotal: number;
  setOrderTotal: (value: number) => void;
  todayTotal: number;
  setTodayTotal: (value: number) => void;
  monthlyInstallment: number;
  setMonthlyInstallment: (value: number) => void;
  orderCurrency: CurrencyCodeEnum;
  setOrderCurrency: (value: CurrencyCodeEnum) => void;
};

type StorefrontCartProviderProps = {
  children: ReactNode;
};

const StorefrontCartContext = createContext<StorefrontCartContextType>({
  catalogCart: [],
  setCatalogCart: () => {},
  assetCart: [],
  setAssetCart: () => {},
  canConfigureWinZtp: false,
  setCanConfigureWinZtp: () => {},
  canConfigureWinAutopilot: false,
  setCanConfigureWinAutopilot: () => {},
  catalogShippingCost: undefined,
  setCatalogShippingCost: () => {},
  warehouseShippingCost: undefined,
  setWarehouseShippingCost: () => {},
  orderTotal: 0,
  setOrderTotal: () => {},
  todayTotal: 0,
  setTodayTotal: () => {},
  monthlyInstallment: 0,
  setMonthlyInstallment: () => {},
  orderCurrency: CurrencyCodeEnum.Usd,
  setOrderCurrency: () => {},
});

export const StorefrontCartProvider = ({
  children,
}: StorefrontCartProviderProps) => {
  const [catalogCart, setCatalogCart] = useState<CatalogCartEntry[]>([]);
  const [assetCart, setAssetCart] = useState<AssetCartEntry[]>([]);

  const [canConfigureWinZtp, setCanConfigureWinZtp] = useState(false);
  const [canConfigureWinAutopilot, setCanConfigureWinAutopilot] =
    useState(false);

  const [orderTotal, setOrderTotal] = useState(0);
  const [todayTotal, setTodayTotal] = useState(0);
  const [monthlyInstallment, setMonthlyInstallment] = useState(0);

  const [catalogShippingCost, setCatalogShippingCost] = useState<
    number | undefined
  >();

  const [warehouseShippingCost, setWarehouseShippingCost] = useState<
    number | undefined
  >();

  const [orderCurrency, setOrderCurrency] = useState<CurrencyCodeEnum>(
    CurrencyCodeEnum.Usd
  );

  return (
    <StorefrontCartContext.Provider
      value={{
        catalogCart,
        setCatalogCart: useCallback(cart => setCatalogCart(cart), []),
        assetCart,
        setAssetCart: useCallback(cart => setAssetCart(cart), []),
        canConfigureWinZtp,
        setCanConfigureWinZtp: useCallback(
          value => setCanConfigureWinZtp(value),
          []
        ),
        canConfigureWinAutopilot,
        setCanConfigureWinAutopilot: useCallback(
          value => setCanConfigureWinAutopilot(value),
          []
        ),
        catalogShippingCost,
        setCatalogShippingCost: useCallback(
          value => setCatalogShippingCost(value),
          []
        ),
        warehouseShippingCost,
        setWarehouseShippingCost: useCallback(
          value => setWarehouseShippingCost(value),
          []
        ),
        orderTotal,
        setOrderTotal: useCallback(value => setOrderTotal(value), []),
        todayTotal,
        setTodayTotal: useCallback(value => setTodayTotal(value), []),
        monthlyInstallment,
        setMonthlyInstallment: useCallback(
          value => setMonthlyInstallment(value),
          []
        ),
        orderCurrency,
        setOrderCurrency: useCallback(value => setOrderCurrency(value), []),
      }}>
      {children}
    </StorefrontCartContext.Provider>
  );
};

export const useStorefrontCart = () => {
  const { t } = useTranslation('common');

  const { showSuccessToast, openGlobalDrawer } = useGlobalUI();
  const {
    storefrontWarehouseShippingFee,
    warrantyUpgradePreference: warrantyUpgradePreferenceEnabled,
    storefrontRecommendedAccessoriesInShoppingStep,
  } = useFlags();

  const organizationId = useGetOrganizationId();
  const { data: orgAdditionalInfo } = useGetOrganizationAdditionalInfoQuery({
    organizationId,
  });

  const {
    catalogCart,
    setCatalogCart,
    assetCart,
    setAssetCart,
    canConfigureWinZtp,
    setCanConfigureWinZtp,
    canConfigureWinAutopilot,
    setCanConfigureWinAutopilot,
    catalogShippingCost,
    setCatalogShippingCost,
    warehouseShippingCost,
    setWarehouseShippingCost,
    orderTotal,
    setOrderTotal,
    todayTotal,
    setTodayTotal,
    monthlyInstallment,
    setMonthlyInstallment,
    orderCurrency,
    setOrderCurrency,
  } = useContext(StorefrontCartContext);

  const addCatalogCartEntry = useCallback(
    (product: Product, showDrawer?: boolean) => {
      // Create cart entry using shared utility for consistent warranty selection logic
      // @see src/components/Storefront/utils/cartHelpers.ts
      const newEntry = createCatalogCartEntry(product, {
        shouldUpgradeWarranty:
          orgAdditionalInfo?.warrantyUpgradePreference &&
          warrantyUpgradePreferenceEnabled,
      });

      // Add the entry to the end of the cart
      const cart: CatalogCartEntry[] = [...catalogCart, newEntry];
      setCatalogCart(cart);

      // show drawer or toast of success
      if (
        showDrawer &&
        storefrontRecommendedAccessoriesInShoppingStep &&
        getItemRelatedProducts(
          product,
          RelatedProductCategoryNameEnum.Accessories
        ).length > 0 // at least for now (while we don't have other things to fill up the drawer), it will only be opened if there are accessories to show
      ) {
        openGlobalDrawer(<StoreDrawerContent item={product} />, {
          width: '50rem',
        });
      } else {
        showSuccessToast({
          id: 'added-catalog-product-toast',
          title: t('storefront.catalog.itemAdded'),
          message: t('storefront.catalog.itemAddedMessage', {
            name: product.name,
          }),
          testId: 'added-catalog-product-toast',
        });
      }
    },
    [
      catalogCart,
      setCatalogCart,
      showSuccessToast,
      openGlobalDrawer,
      storefrontRecommendedAccessoriesInShoppingStep,
      t,
      orgAdditionalInfo?.warrantyUpgradePreference,
      warrantyUpgradePreferenceEnabled,
    ]
  );

  const removeCatalogCartEntry = useCallback(
    (product: Product) => {
      // remove the entry from the cart, independent of its amount
      const cart: CatalogCartEntry[] = catalogCart.filter(
        entry => entry.item.id !== product.id
      );

      setCatalogCart(cart);

      showSuccessToast({
        id: 'removed-catalog-product-toast',
        title: t('storefront.catalog.itemRemoved'),
        message: t('storefront.catalog.itemRemovedMessage', {
          name: product.name,
        }),
        testId: 'removed-catalog-product-toast',
      });
    },
    [catalogCart, setCatalogCart, showSuccessToast, t]
  );

  const addAssetCartEntry = useCallback(
    (item: StorefrontAsset) => {
      // add the entry to the end of the cart
      const cart: AssetCartEntry[] = [
        ...assetCart,
        {
          item: { ...item },
        },
      ];

      setAssetCart(cart);

      showSuccessToast({
        id: 'added-asset-success-toast',
        title: t('storefront.asset.itemAdded'),
        message: t('storefront.asset.itemAddedMessage', { name: item.name }),
        testId: 'added-asset-product-toast',
      });
    },
    [assetCart, setAssetCart, showSuccessToast, t]
  );

  const removeAssetCartEntry = useCallback(
    (item: StorefrontAsset) => {
      // remove the entry from the cart
      const cart: AssetCartEntry[] = assetCart.filter(
        entry => entry.item.id !== item.id
      );

      setAssetCart(cart);

      showSuccessToast({
        id: 'removed-asset-success-toast',
        title: t('storefront.asset.itemRemoved'),
        message: t('storefront.asset.itemRemovedMessage', { name: item.name }),
        testId: 'removed-asset-product-toast',
      });
    },
    [assetCart, setAssetCart, showSuccessToast, t]
  );

  const addWarrantyToEntry = useCallback(
    (itemId: string, warranty: Product) => {
      const newCart = catalogCart.map(entry => {
        if (entry.item.id === itemId) {
          return { ...entry, selectedWarranty: warranty };
        }

        return { ...entry };
      });

      setCatalogCart(newCart);
    },
    [catalogCart, setCatalogCart]
  );

  const incrementEntryQuantity = useCallback(
    (itemId: string) => {
      // increment the quantity of the entry
      const newCart = catalogCart.map(entry => {
        if (entry.item.id === itemId) {
          return { ...entry, quantity: entry.quantity + 1 };
        }

        return { ...entry };
      });

      setCatalogCart(newCart);
    },
    [catalogCart, setCatalogCart]
  );

  const decrementEntryQuantity = useCallback(
    (itemId: string) => {
      // decrement the quantity of the entry
      const newCart = catalogCart.map(entry => {
        if (entry.item.id === itemId) {
          return { ...entry, quantity: entry.quantity - 1 };
        }

        return { ...entry };
      });

      // if the new quantity is 0, remove the entry from the cart
      const updatedEntry = newCart.find(entry => entry.item.id === itemId);
      if (updatedEntry?.quantity === 0) {
        removeCatalogCartEntry(updatedEntry.item);
      } else {
        // otherwise, just save the cart with the decremented quantity
        setCatalogCart(newCart);
      }
    },
    [catalogCart, setCatalogCart, removeCatalogCartEntry]
  );

  const itemsInCart =
    assetCart?.length +
    catalogCart?.reduce((acc, entry) => {
      // Add warranty as item only if the warranty selected is different from the default warranty
      if (
        entry.selectedWarranty?.id &&
        entry.selectedWarranty.id !==
          entry.item.availableWarranties?.defaultWarrantyId
      ) {
        return acc + entry.quantity * 2;
      }
      return acc + entry.quantity;
    }, 0);

  const hasOnlyAssetsInCart = assetCart.length > 0 && catalogCart.length === 0;
  const cartContainsWarehouseAsset = assetCartContainsWarehouseAsset(assetCart);

  const hasOnlyCatalogProductsInCart =
    catalogCart.length > 0 && assetCart.length === 0;

  // If we're helping with shipping, we need to know the shipping address
  // Current cases are as follows:
  // - If they are buying items from the catalog
  // - If they are selecting assets that are stored at the warehouse
  const cartRequiresShippingAddress =
    catalogCart.length > 0 || cartContainsWarehouseAsset;

  const cartRequiresCatalogShippingMethod = catalogCart.length > 0;

  const cartRequiresWarehouseShippingMethod =
    storefrontWarehouseShippingFee && cartContainsWarehouseAsset;

  const cartRequiresPayment = todayTotal > 0;

  const cartRequiresTax =
    catalogCart.length > 0 ||
    (storefrontWarehouseShippingFee && cartContainsWarehouseAsset);

  const hasMixedCart = catalogCart.length > 0 && assetCart.length > 0;

  /** Subtotal is the sum of the prices of all items in the cart, without shipping costs or tax. */
  const subTotalPrice = catalogCart.reduce((acc, entry) => {
    const warrantyPrice = entry.selectedWarranty?.price ?? 0;
    return acc + ((entry.item.price ?? 0) + warrantyPrice) * entry.quantity;
  }, 0);

  return {
    catalogCart,
    setCatalogCart,
    assetCart,
    setAssetCart,
    addCatalogCartEntry,
    removeCatalogCartEntry,
    addAssetCartEntry,
    removeAssetCartEntry,
    addWarrantyToEntry,
    incrementEntryQuantity,
    decrementEntryQuantity,
    itemsInCart,
    hasOnlyAssetsInCart,
    cartRequiresShippingAddress,
    cartRequiresCatalogShippingMethod,
    cartRequiresWarehouseShippingMethod,
    cartRequiresPayment,
    cartRequiresTax,
    canConfigureWinZtp,
    setCanConfigureWinZtp,
    canConfigureWinAutopilot,
    setCanConfigureWinAutopilot,
    catalogShippingCost,
    setCatalogShippingCost,
    warehouseShippingCost,
    setWarehouseShippingCost,
    hasOnlyCatalogProductsInCart,
    hasMixedCart,
    orderTotal,
    setOrderTotal,
    todayTotal,
    setTodayTotal,
    monthlyInstallment,
    setMonthlyInstallment,
    /** Subtotal is the sum of the prices of all items in the cart, without catalog shipping cost or tax. */
    subTotalPrice,
    orderCurrency,
    setOrderCurrency,
  };
};
